import React, { forwardRef, useState, useEffect } from 'react';
import { BeautifulMentionComponentProps } from 'lexical-beautiful-mentions';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Box,
  Text,
  Image,
  useDisclosure,
  Portal
} from '@chakra-ui/react';
import defaultAvatar from '../../../../assets/small_default_optimized.png';
import VerifiedIcon from '../../../../assets/verified-icon.svg';
import Button from 'src/components/Button';
import { Link } from 'react-router-dom';
import { useAtom } from 'jotai';
import { profileAtom } from 'src/components/Settings/atom';
import { useFollowUserMutation } from 'src/apis/queries/useFollowUserMutation';
import { useEditorState } from '../../BaseEditor/hooks/useBaseEditorFormatState';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

type UserMentionData = {
  id: string;
  name: string;
  username: string;
  avatarUrl?: string;
  role?: string;
  isVerified?: boolean;
  isFollowing?: boolean;
};

export const MentionComponent = forwardRef<
  HTMLSpanElement,
  BeautifulMentionComponentProps<UserMentionData>
>(({ trigger, value, data, children, ...other }, ref) => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [isFollowing, setIsFollowing] = useState(false);
  const [profile] = useAtom(profileAtom);
  const { mutate: followUser, isPending: isFollowingUser } = useFollowUserMutation(undefined);
  const [editor] = useLexicalComposerContext();

  const { isEditable } = useEditorState(editor as any);

  const username = data?.username || value.replace('@', '');
  const profileUrl = `/${data?.display}`;

  useEffect(() => {
    if (data?.isFollowing !== undefined) {
      setIsFollowing(data.isFollowing);
    }
  }, [data?.isFollowing]);

  const handleFollowUser = () => {
    followUser({
      userId: data?.id,
      isFollowing: isFollowing,
      writerUsername: data?.username
    });
    setIsFollowing(!isFollowing);
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom"
      closeOnBlur={true}
      isLazy
      gutter={8}
    >
      <PopoverTrigger>
        <span
          {...other}
          ref={ref}
          className="text-growth-green font-[800] text-sm cursor-pointer hover:opacity-70 transition-all duration-200"
          onClick={onToggle}
        >
          {trigger}
          {value}
        </span>
      </PopoverTrigger>

      {isEditable || (
        <Portal>
          <PopoverContent
            width="191px"
            borderRadius="xl"
            padding={0}
            overflow="hidden"
            zIndex={2000}
          >
            <PopoverBody padding={0}>
              <Box borderRadius="xl" overflow="hidden">
                <Box py={4} px={4} className="flex flex-col items-center">
                  <Link className="flex items-center w-full cursor-pointer" to={profileUrl}>
                    <Image
                      src={data?.imgUrl || defaultAvatar}
                      alt={data?.name || value}
                      boxSize="36px"
                      borderRadius="full"
                      objectFit="cover"
                      flexShrink={0}
                      fallbackSrc={defaultAvatar}
                    />
                    <Box ml={2} className="flex flex-col" minW={0}>
                      <Box display={'flex'} alignItems="center">
                        <Text
                          fontWeight="600"
                          fontSize="xs"
                          lineHeight={'100%'}
                          _hover={{ color: 'growth-green' }}
                          isTruncated
                          transition={'0.2s'}
                        >
                          {data?.name || value}
                        </Text>
                        {data?.isVerified && (
                          <Image ml={1} className="w-4 h-4" src={VerifiedIcon} alt="" />
                        )}
                      </Box>
                      <Text fontSize="10px" lineHeight={'100%'} color="dim-gray" mt="1">
                        @{username}
                      </Text>
                    </Box>
                  </Link>

                  {data?.id !== profile.id && (
                    <Button
                      className="w-full mt-2 h-9"
                      variant={isFollowing ? 'outline' : 'primary'}
                      onClick={handleFollowUser}
                      isLoading={isFollowingUser}
                    >
                      {isFollowing ? 'Following' : 'Follow'}
                    </Button>
                  )}
                </Box>
              </Box>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      )}
    </Popover>
  );
});

export default MentionComponent;
